<script lang="ts">
  import { mdiCalendarCheck, mdiCheckAll, mdiCloseCircleOutline } from "@mdi/js"
  import { onDestroy, onMount, type Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import type {
    PublicAppointmentParticipantInput,
    PublicAppointmentSummary,
    PublicAppointmentSlot,
  } from "../../../../../../../../api/public/appointment-portal"
  import {
    buildAppointmentPortalEmailPlaceholder,
    buildAppointmentPortalPhonePlaceholder,
    type AppointmentPortalContactMethodHints,
  } from "../../../../../../_shared/_logic/contact-method-hints"
  import {
    DEADLINE_WARNING_TEXT,
    slotHasDeadlineWarning,
  } from "../../_shared/_logic/appointment-portal"
  import SlotSummary from "../../_shared/_modules/slot-summary"
  import { normalizePrivacyUrl } from "../../_shared/_logic/contact-privacy"

  type PortalAction = "confirm" | "book" | "cancel"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type ParticipantFormModel = {
    display_name: string
    email: string
    phone: string
    customer_role_hint: "TENANT" | "MANAGER" | "OWNER" | "OTHER"
    note: string
    privacy_notice_acknowledged: boolean
    idempotency_key: string
  }

  let {
    action,
    organizationDisplayName = "dem Betrieb",
    organizationPrivacyUrl = null,
    contactMethodHints = null,
    selectedSlot = null,
    onsubmit,
    oncompleted = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: {
    action: PortalAction
    organizationDisplayName?: string
    organizationPrivacyUrl?: string | null
    contactMethodHints?: AppointmentPortalContactMethodHints | null
    selectedSlot?: PublicAppointmentSlot | PublicAppointmentSummary | null
    onsubmit: (participant: PublicAppointmentParticipantInput) => Promise<boolean | void> | void
    oncompleted?: (() => void) | null
  } & OverlayHostProps = $props()

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)
  let validationError = $state<string | null>(null)

  const escapeHtml = (value: string): string =>
    value.replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char] ?? char,
    )

  const buildPrivacyNoticePlaceholder = (privacyUrl: string | null): string => {
    const privacyNoticeLabel = privacyUrl
      ? `<a href="${escapeHtml(privacyUrl)}" target="_blank" rel="noreferrer">Datenschutzhinweise</a>`
      : "Datenschutzhinweise"

    return [
      `Ich habe die ${privacyNoticeLabel} zur Kenntnis genommen und bin damit einverstanden, dass die von mir gegebenenfalls angegebenen Kontaktdaten für die Terminkommunikation genutzt werden dürfen.`,
      "Dazu gehören Benachrichtigungen per E-Mail, Telefon, SMS und – sofern registriert – WhatsApp.",
      "Eine Nutzung zu Werbezwecken erfolgt nicht.",
      "Kontaktwege können jederzeit im Terminportal unter „Kontaktwege“ geändert oder deaktiviert werden.",
    ].join(" ")
  }

  const buildIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID()
    }

    return `portal-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  const initialForm = (): ParticipantFormModel => ({
    display_name: "",
    email: "",
    phone: "",
    customer_role_hint: "TENANT",
    note: "",
    privacy_notice_acknowledged: false,
    idempotency_key: buildIdempotencyKey(),
  })

  const submitLabel = $derived(
    submitting
      ? "Wird gespeichert"
      : action === "cancel"
        ? "stornieren"
        : action === "book"
          ? "bestätigen"
          : "bestätigen",
  )
  const submitIcon = $derived(
    action === "cancel"
      ? mdiCloseCircleOutline
      : action === "book"
        ? mdiCalendarCheck
        : mdiCheckAll,
  )
  const introText = $derived(
    action === "cancel"
      ? "Bitte geben Sie kurz an, wer den Termin warum storniert."
      : "Bitte geben Sie kurz an, wer den Termin wahrnimmt.",
  )
  const safeOrganizationPrivacyUrl = $derived(normalizePrivacyUrl(organizationPrivacyUrl))
  const privacyNoticePlaceholder = $derived(
    buildPrivacyNoticePlaceholder(safeOrganizationPrivacyUrl),
  )
  const emailPlaceholder = $derived(buildAppointmentPortalEmailPlaceholder(contactMethodHints))
  const phonePlaceholder = $derived(buildAppointmentPortalPhonePlaceholder(contactMethodHints))
  const showDeadlineWarning = $derived(action === "book" && slotHasDeadlineWarning(selectedSlot))

  const trimOptional = (value: string) => {
    const text = value.trim()

    return text ? text : null
  }

  const showValidationError = (message: string) => {
    validationError = message
    newNotification({
      class: "error",
      html: message,
    })
  }

  const validateParticipant = ({ data }: { data: ParticipantFormModel }) => {
    if (!data.display_name.trim()) {
      showValidationError("Bitte geben Sie Ihren Namen an.")

      return false
    }

    if (!data.privacy_notice_acknowledged) {
      showValidationError(
        `Bitte bestätigen Sie, dass Sie die Datenschutzhinweise von ${organizationDisplayName} zur Kenntnis genommen haben.`,
      )

      return false
    }

    validationError = null

    return true
  }

  const submitParticipant = async (data: ParticipantFormModel) => {
    await onsubmit({
      display_name: data.display_name.trim(),
      email: trimOptional(data.email),
      phone: trimOptional(data.phone),
      customer_role_hint: action === "cancel" ? null : data.customer_role_hint,
      note: trimOptional(data.note),
      idempotency_key: data.idempotency_key,
    })
    onremoveOverlay?.()
    oncompleted?.()
  }

  const close = () => {
    if (submitting) return

    onremoveOverlay?.()
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="appointment-action-details overlay-form">
  {#if showDeadlineWarning}
    <div class="deadline-warning" role="alert">
      <strong>Termin außerhalb der Frist</strong>
      <p>{DEADLINE_WARNING_TEXT}</p>
    </div>
  {/if}
  <p class="intro">{introText}</p>
  {#if validationError}
    <p class="field-error">{validationError}</p>
  {/if}
  <FormWrapper
    theme="light"
    mode="create"
    getInitObject={initialForm}
    validateData={validateParticipant}
    onSubmit={submitParticipant}
    showReset={false}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, submitting } = ctx}
  <section class="field-stack">
    <Input
      id="appointment-portal-display-name"
      placeholder="Vollständiger Name"
      bind:value={data.display_name}
      required={true}
      disabled={submitting}
      brightBackground={true}
      autocomplete="name"
    />
    <div class="row">
      <Input
        id="appointment-portal-email"
        type="email"
        placeholder={emailPlaceholder}
        bind:value={data.email}
        disabled={submitting}
        brightBackground={true}
        autocomplete="email"
        tooltip="Wir verwenden Ihre E-Mail-Adresse, um Ihnen eine Bestätigung und ggf. weitere Informationen zum Termin zu senden. Außerdem nutzen wir Sie, um zukünftige Termine einfacher abzustimmen."
      />
      <Input
        id="appointment-portal-phone"
        type="tel"
        placeholder={phonePlaceholder}
        bind:value={data.phone}
        disabled={submitting}
        brightBackground={true}
        autocomplete="tel"
        tooltip="Wir verwenden Ihre Telefonnummer, um Ihnen ggf. weitere Informationen zum Termin zu senden oder Sie anzurufen, falls es Rückfragen gibt. Außerdem nutzen wir Sie, um zukünftige Termine einfacher abzustimmen."
      />
    </div>
    {#if action !== "cancel"}
      <Input
        id="appointment-portal-customer-role"
        type="select"
        placeholder="Rolle"
        bind:value={data.customer_role_hint}
        options={[
          { value: "TENANT", label: "Mieter" },
          { value: "MANAGER", label: "Verwaltung" },
          { value: "OWNER", label: "Eigentümer" },
          { value: "OTHER", label: "Sonstige Rolle" },
        ]}
        disabled={submitting}
        brightBackground={true}
        tooltip="Die Rolle wird nur verwendet, wenn der Betrieb diesen Ansprechpartner neu anlegen muss."
      />
    {/if}
    <Input
      id="appointment-portal-note"
      type="textarea"
      placeholder={action === "cancel"
        ? "Grund für die Stornierung (optional)"
        : "Anmerkungen wie beispielsweise Zugangsinformationen (optional)"}
      bind:value={data.note}
      disabled={submitting}
      brightBackground={true}
    />
    {#if selectedSlot}
      <SlotSummary slot={selectedSlot} darkMode={false} />
    {/if}
    <ActionBox label="Datenschutz" darkMode={false}>
      <div class="privacy-content form light" aria-label="Datenschutzhinweis">
        <p>
          Mit dem Absenden übermitteln Sie Ihre Angaben an {organizationDisplayName}. KehrBlatt
          stellt dafür die technische Plattform bereit. Hinweise zur Verarbeitung Ihrer Daten finden
          Sie in den Datenschutzhinweisen von {organizationDisplayName}.
        </p>
        <Input
          id="appointment-portal-privacy-notice"
          type="checkbox"
          placeholder={privacyNoticePlaceholder}
          bind:value={data.privacy_notice_acknowledged}
          disabled={submitting}
          brightBackground={true}
        />
      </div>
    </ActionBox>
  </section>
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="primaryButton"
    icon={submitIcon}
    content={submitLabel}
    onClick={() => submitForm?.()}
    disabled={submitting}
    fullWidth={true}
  />
{/snippet}

<style>
  .appointment-action-details {
    display: grid;
    gap: 1rem;
    padding-bottom: 1rem;
  }

  .intro,
  .field-error,
  .deadline-warning p {
    margin: 0;
  }

  .deadline-warning {
    display: grid;
    gap: 0.35rem;
    border: 1px solid #dc2626;
    background: #fff7f7;
    color: #7f1d1d;
    padding: 0.85rem;
    line-height: 1.42;
  }

  .deadline-warning strong {
    font-weight: 900;
  }

  .intro {
    color: var(--bg-200);
    line-height: 1.45;
  }

  .field-error {
    color: #b42318;
    font-weight: 800;
  }

  .field-stack {
    display: grid;
    gap: 0.9rem;
  }

  .privacy-content {
    display: grid;
    gap: 0.65rem;

    p {
      margin: 0;
      color: var(--bg-200);
      font-size: 0.95rem;
      line-height: 1.42;
    }

    :global(.checkbox-label a) {
      color: var(--text-100);
      font-weight: 800;
      text-decoration: underline;
      text-decoration-thickness: 0.08em;
      text-underline-offset: 0.16em;
    }
  }

  :global(.appointment-action-details .formActions) {
    display: none;
  }
</style>
